'use client'

import type { MuxPlayerRefAttributes } from '@mux/mux-player-react'
import type { MuxPlaybackError } from './playback-diagnostics'
import type { ProgressInfo } from './use-progress-reporting'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAnalytics } from '../analytics/use-analytics'
import { diagnoseManifest, reportPlaybackError } from './playback-diagnostics'
import { useProgressReporting } from './use-progress-reporting'

/** Shown while the token loads and while the Mux bundle does. */
function PlayerLoading() {
  const t = useTranslations('learn.player')
  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-2xl border border-border/60 bg-muted/40 text-sm text-muted-foreground">
      <Loader2 className="mr-2 size-4 animate-spin" />
      {t('loading')}
    </div>
  )
}

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), {
  ssr: false,
  loading: () => <PlayerLoading />,
})

interface Props {
  videoId: string
  title?: string
  autoPlay?: boolean
  onEnded?: () => void
  onPlay?: () => void
  onProgress?: (info: ProgressInfo) => void
  initialPositionSeconds?: number
  /** Which lesson this is, for `lesson_video_started` / `lesson_completed`. */
  analytics?: { courseId: string, lessonId: string }
}

interface TokenResponse {
  token: string
  playbackId: string
  expiresAt: string
}

const REFRESH_LEAD_MS = 5 * 60 * 1000
/**
 * How many times a "stream fine, player choked" error triggers a silent
 * player remount before we give up and show the retryable error state.
 * Reset whenever playback actually resumes.
 */
const MAX_AUTO_RECOVERIES = 2

/**
 * What went wrong, as a kind rather than a sentence. The handlers that set it
 * run inside effects and callbacks; translating there would put `t` in their
 * dependency lists and let a locale change re-run the token fetch. Kinds are
 * translated where they render.
 */
type PlayerErrorKind
  = | 'couldNotPlay'
    | 'interrupted'
    | 'notAuthorized'
    | 'unavailable'
    | 'temporarilyUnavailable'
    | 'checkConnection'
    | 'tokenUnavailable'
    | 'couldNotLoad'

interface PlayerError {
  kind: PlayerErrorKind
  /** Retryable errors render a "Try again" button that reloads token + player. */
  retryable: boolean
}

export function LessonPlayer({
  videoId,
  title,
  autoPlay,
  onEnded,
  onPlay,
  onProgress,
  initialPositionSeconds,
  analytics,
}: Props) {
  const t = useTranslations('learn.player')
  const [data, setData] = useState<TokenResponse | null>(null)
  const [error, setError] = useState<PlayerError | null>(null)
  // Bumping playerKey remounts <MuxPlayer> with the same (still valid) token —
  // the silent auto-recovery path. Bumping loadEpoch re-runs the token fetch
  // AND remounts — the manual "Try again" path.
  const [playerKey, setPlayerKey] = useState(0)
  const [loadEpoch, setLoadEpoch] = useState(0)
  const autoRecoveriesRef = useRef(0)
  // Where to resume after a recovery remount; beats initialPositionSeconds.
  const resumePositionRef = useRef<number | null>(null)
  const refreshTimer = useRef<number | null>(null)
  const playerRef = useRef<MuxPlayerRefAttributes | null>(null)
  // Keep the current token/playbackId reachable from the player's error handler
  // without re-subscribing it on every refresh.
  const dataRef = useRef<TokenResponse | null>(null)
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const { track } = useAnalytics()
  const courseId = analytics?.courseId
  const lessonId = analytics?.lessonId
  // Once per lesson: a recovery remount or a pause/resume replays `play`.
  const startedLessonRef = useRef<string | null>(null)
  const handlePlay = useCallback(() => {
    if (courseId && lessonId && startedLessonRef.current !== lessonId) {
      startedLessonRef.current = lessonId
      track('lesson_video_started', { course_id: courseId, lesson_id: lessonId })
    }
    onPlay?.()
  }, [courseId, lessonId, onPlay, track])
  const handleLessonEnded = useCallback(() => {
    if (courseId && lessonId) {
      const duration = playerRef.current?.duration
      track('lesson_completed', {
        course_id: courseId,
        lesson_id: lessonId,
        duration_seconds: Number.isFinite(duration) ? Math.round(duration ?? 0) : 0,
      })
    }
    onEnded?.()
  }, [courseId, lessonId, onEnded, track])

  const { handleTimeUpdate, handlePause, handleEnded }
    = useProgressReporting(playerRef, onProgress, handleLessonEnded)

  // Playback resumed — the stream is healthy again, so future interruptions
  // get a fresh recovery budget.
  const handlePlaying = useCallback(() => {
    autoRecoveriesRef.current = 0
  }, [])

  // Manual retry: full reload (fresh token + player remount).
  const handleRetry = useCallback(() => {
    autoRecoveriesRef.current = 0
    setError(null)
    setLoadEpoch(e => e + 1)
  }, [])

  // On player error: beacon the real MediaError to our first-party endpoint,
  // then probe the manifest to classify the failure. If the stream is fine and
  // the player just choked (transient network blip mid-stream), silently
  // remount and resume — up to MAX_AUTO_RECOVERIES — instead of dead-ending
  // the learner on a "please refresh" message.
  const handlePlaybackError = useCallback((event: unknown) => {
    const detail = (event as CustomEvent | undefined)?.detail as MuxPlaybackError | undefined
    const current = dataRef.current

    // Capture the position synchronously — the player may tear down while the
    // async diagnosis below runs.
    const position = playerRef.current?.currentTime
    if (Number.isFinite(position) && (position ?? 0) > 0)
      resumePositionRef.current = position as number

    reportPlaybackError({
      videoId,
      playbackId: current?.playbackId,
      code: detail?.code,
      muxCode: detail?.muxCode,
      category: detail?.errorCategory,
      fatal: detail?.fatal,
      message: detail?.message?.slice(0, 500),
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      downlink: (navigator as Navigator & { connection?: { downlink?: number } })
        .connection
        ?.downlink,
      autoRecoveryAttempt: autoRecoveriesRef.current,
    })

    if (!current) {
      setError({ kind: 'couldNotPlay', retryable: true })
      return
    }
    void (async () => {
      const diagnosis = await diagnoseManifest(current.playbackId, current.token)
      if (diagnosis.kind === 'reachable') {
        if (autoRecoveriesRef.current < MAX_AUTO_RECOVERIES) {
          autoRecoveriesRef.current += 1
          setPlayerKey(k => k + 1)
          return
        }
        console.error('[lesson-player] playback failed although the manifest is reachable', {
          videoId,
          playbackId: current.playbackId,
          muxCode: detail?.muxCode,
        })
        setError({ kind: 'interrupted', retryable: true })
        return
      }
      if (diagnosis.kind === 'rejected') {
        console.error('[lesson-player] Mux rejected playback', {
          videoId,
          playbackId: current.playbackId,
          status: diagnosis.status,
          muxError: diagnosis.body,
        })
        setError(
          diagnosis.status === 403
            ? { kind: 'notAuthorized', retryable: true }
            : diagnosis.status === 404
              ? { kind: 'unavailable', retryable: false }
              : { kind: 'temporarilyUnavailable', retryable: true },
        )
        return
      }
      console.error('[lesson-player] playback diagnostic request failed', {
        videoId,
        playbackId: current.playbackId,
        err: diagnosis.error,
      })
      setError({ kind: 'checkConnection', retryable: true })
    })()
  }, [videoId])

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const res = await fetch(`/api/mux/playback-token/${videoId}`, {
          cache: 'no-store',
        })
        if (!res.ok) {
          if (!cancelled)
            setError({ kind: 'tokenUnavailable', retryable: true })
          return
        }
        const json = (await res.json()) as TokenResponse
        if (cancelled)
          return
        setData(json)
        setError(null)
        scheduleRefresh(json.expiresAt)
      }
      catch {
        if (!cancelled)
          setError({ kind: 'couldNotLoad', retryable: true })
      }
    }

    function scheduleRefresh(expiresAt: string) {
      if (refreshTimer.current !== null) {
        window.clearTimeout(refreshTimer.current)
      }
      const ms = new Date(expiresAt).getTime() - Date.now() - REFRESH_LEAD_MS
      if (ms <= 0)
        return
      refreshTimer.current = window.setTimeout(load, ms)
    }

    void load()
    return () => {
      cancelled = true
      if (refreshTimer.current !== null) {
        window.clearTimeout(refreshTimer.current)
        refreshTimer.current = null
      }
    }
  }, [videoId, loadEpoch])

  if (error) {
    const message: Record<PlayerErrorKind, string> = {
      couldNotPlay: t('errors.couldNotPlay'),
      interrupted: t('errors.interrupted'),
      notAuthorized: t('errors.notAuthorized'),
      unavailable: t('errors.unavailable'),
      temporarilyUnavailable: t('errors.temporarilyUnavailable'),
      checkConnection: t('errors.checkConnection'),
      tokenUnavailable: t('errors.tokenUnavailable'),
      couldNotLoad: t('errors.couldNotLoad'),
    }
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        <div className="flex items-center">
          <AlertCircle className="mr-2 size-4 shrink-0" />
          {message[error.kind]}
        </div>
        {error.retryable && (
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            {t('retry')}
          </button>
        )}
      </div>
    )
  }

  if (!data)
    return <PlayerLoading />

  // After a recovery remount, resume where playback broke; otherwise honor the
  // saved progress passed in by the page.
  const startTime = resumePositionRef.current
    ?? (initialPositionSeconds && initialPositionSeconds > 0 ? initialPositionSeconds : undefined)

  return (
    <MuxPlayer
      key={playerKey}
      ref={playerRef}
      streamType="on-demand"
      playbackId={data.playbackId}
      tokens={{ playback: data.token }}
      metadata={title ? { video_title: title } : undefined}
      // Resume playing automatically after a recovery remount — the learner
      // already pressed play; making them press it again reads as broken.
      autoPlay={autoPlay || playerKey > 0}
      startTime={startTime}
      onEnded={handleEnded}
      onPlay={handlePlay}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onTimeUpdate={handleTimeUpdate}
      onError={handlePlaybackError}
      className="aspect-video w-full overflow-hidden rounded-2xl"
    />
  )
}
